import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * Right-side slide-over panel. Body-level portal so it's not
 * constrained by parent overflow/transform. Escape closes it.
 *
 * Props:
 *   open       — boolean
 *   onClose    — function
 *   title      — string
 *   subtitle   — string
 *   children   — React node
 *   dataTestid — string for the panel root
 */
export default function SlideOver({ open, onClose, title, subtitle, children, dataTestid = 'slideover' }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="v2">
      <div className="v2-slideover-overlay" onClick={onClose} data-testid={`${dataTestid}-overlay`} />
      <aside className="v2-slideover" role="dialog" aria-modal="true" data-testid={dataTestid}>
        <header className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h2 className="text-xl font-extrabold text-slate-900 tracking-tight">{title}</h2>
            {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition"
            aria-label="Close"
            data-testid={`${dataTestid}-close`}
          >
            <X className="w-5 h-5" />
          </button>
        </header>
        <div>{children}</div>
      </aside>
    </div>,
    document.body,
  );
}
